import { Form, Input, Modal, Select, Upload } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEVICE_TYPE_OPTIONS } from './HelperModal.mocks';
import { HelperModalForm, HelperModalProps } from './HelperModal.types';
import { HelperModalGlobalStyle, StyledHelperModal } from './HelperModal.styles';
import { KccButton } from 'lib/components/KccButton/KccButton';
import { formatFileSize } from 'app/modules/service/utils/file';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { ALLOWED_DEVICE_IMAGE_TYPES, IS_DESKTOP, MAX_FILE_SIZE } from 'app/modules/service/utils/constants';

export const HelperModal = ({ open, onClose, onConfirm, initialValues }: HelperModalProps): JSX.Element => {
  const { t } = useTranslation();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deviceImage, setDeviceImage] = useState<File | undefined>(undefined);
  const [form] = Form.useForm<HelperModalForm>();
  const deviceType = Form.useWatch('deviceType', form);

  const initialValuesRef = useRef(initialValues);

  useEffect(() => {
    const values = initialValuesRef.current;
    if (!values) return;
    if (values.deviceImage) {
      setDeviceImage(values.deviceImage);
      setPreviewUrl(URL.createObjectURL(values.deviceImage));
    }
    form.setFieldsValue({
      deviceType: values.deviceType,
      deviceName: values.deviceName,
    });
  }, [form]);

  const validateFile = (file: File): boolean => {
    if (!ALLOWED_DEVICE_IMAGE_TYPES.includes(file.type)) {
      setUploadError(t('service.helperModal.uploadError.unsupportedType'));
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(t('service.helperModal.uploadError.fileTooLarge'));
      return false;
    }
    return true;
  };

  const applyFile = (file: File): void => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setUploadError(null);
    setDeviceImage(file);
    form.setFieldValue('deviceImage', file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && validateFile(file)) applyFile(file);
  };

  const handleRemoveFile = (): void => {
    setDeviceImage(undefined);
    form.setFieldValue('deviceImage', undefined);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setUploadError(null);
  };

  const handleBeforeUploadImage = (file: File): false | typeof Upload.LIST_IGNORE => {
    if (!validateFile(file)) return Upload.LIST_IGNORE;
    applyFile(file);
    return false;
  };

  const uploadFileProps = {
    accept: ALLOWED_DEVICE_IMAGE_TYPES.join(','),
    showUploadList: false,
    multiple: false,
    beforeUpload: handleBeforeUploadImage,
  };

  const onFinish = (values: HelperModalForm): void => {
    onConfirm({ ...values, deviceImage });
  };

  const handleConfirm = (): void => {
    form.submit();
  };

  return (
    <>
      <HelperModalGlobalStyle />
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        title={t('service.helperModal.title')}
        width={{
          xs: '100%',
          sm: 560,
          md: 720,
          lg: 800,
          xl: 800,
          xxl: 800,
        }}
        rootClassName="helper-modal"
      >
        <Form form={form} component={false} onFinish={onFinish}>
          <StyledHelperModal>
            <div className="helper-modal__info-alert">
              <SvgIcon name="information" className="helper-modal__info-alert-icon" />
              <p>{t('service.helperModal.infoAlert')}</p>
            </div>

            <div className="helper-modal__scroll-content">
              <div className="helper-modal__content">
                <div className="helper-modal__columns">
                  <div className="helper-modal__column">
                    <p className="helper-modal__column-title">{t('service.helperModal.identifyWithPhoto')}</p>
                    {deviceImage && previewUrl ? (
                      <>
                        {IS_DESKTOP ? (
                          <div className="helper-modal__file-preview">
                            <img src={previewUrl} alt={deviceImage.name} className="helper-modal__file-thumbnail" />
                            <div className="helper-modal__file-info">
                              <span className="helper-modal__file-name">{deviceImage.name}</span>
                              <span className="helper-modal__file-size">{formatFileSize(deviceImage.size)}</span>
                            </div>
                            <KccButton
                              className="helper-modal__file-remove"
                              type="link"
                              onClick={handleRemoveFile}
                              icon={<SvgIcon name="trash" />}
                            />
                          </div>
                        ) : (
                          <div className="helper-modal__mobile-photo-preview">
                            <img src={previewUrl} alt={deviceImage.name} className="helper-modal__mobile-photo-image" />
                            <div className="helper-modal__mobile-upload">
                              <Upload {...uploadFileProps}>
                                <KccButton
                                  className="helper-modal__take-photo-btn"
                                  type="secondary"
                                  block
                                  icon={<SvgIcon name="camera" />}
                                >
                                  {t('service.helperModal.takeANewPhoto')}
                                </KccButton>
                              </Upload>
                              <p className="helper-modal__take-photo-hint">{t('service.helperModal.takeAPhotoHint')}</p>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="helper-modal__desktop-upload">
                          <Upload {...uploadFileProps}>
                            <div
                              className="helper-modal__upload-area"
                              onDragOver={(e): void => e.preventDefault()}
                              onDrop={handleDrop}
                            >
                              <SvgIcon name="upload" className="helper-modal__upload-icon" />
                              <p className="helper-modal__upload-text">{t('service.helperModal.uploadText')}</p>
                              <p className="helper-modal__upload-hint">{t('service.helperModal.uploadHint')}</p>
                            </div>
                          </Upload>
                        </div>
                        <div className="helper-modal__mobile-upload">
                          <Upload {...uploadFileProps}>
                            <KccButton
                              className="helper-modal__take-photo-btn"
                              type="secondary"
                              block
                              icon={<SvgIcon name="camera" />}
                            >
                              {t('service.helperModal.takeAPhoto')}
                            </KccButton>
                          </Upload>
                          <p className="helper-modal__take-photo-hint">{t('service.helperModal.takeAPhotoHint')}</p>
                        </div>
                      </>
                    )}
                    {uploadError && (
                      <div className="helper-modal__upload-error-container">
                        <SvgIcon name="warningCritical" className="helper-modal__upload-error-icon" />
                        <p className="helper-modal__upload-error">{uploadError}</p>
                      </div>
                    )}
                  </div>

                  <span className="helper-modal__or-divider">or</span>

                  <div className="helper-modal__column">
                    <p className="helper-modal__column-title">{t('service.helperModal.selectFromDeviceList')}</p>
                    <Form.Item name="deviceType" style={{ marginBottom: 0 }}>
                      <Select
                        allowClear
                        className="helper-modal__select"
                        placeholder={t('service.helperModal.deviceTypePlaceholder')}
                        suffixIcon={<SvgIcon name="chevronDown" />}
                        popupClassName="helper-modal__select-popup"
                        options={DEVICE_TYPE_OPTIONS}
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>

              <hr className="helper-modal__divider" />

              <label className="helper-modal__device-name-label">
                {t('service.helperModal.deviceName')}
                <span className="helper-modal__optional">{t('service.helperModal.optional')}</span>
              </label>
              <Form.Item name="deviceName">
                <Input />
              </Form.Item>
            </div>

            <div className="helper-modal__footer">
              <KccButton className="helper-modal__cancel-btn" type="secondary" onClick={onClose}>
                {t('common.cancel')}
              </KccButton>
              <KccButton
                className="helper-modal__confirm-btn"
                type="primary"
                disabled={!deviceImage && !deviceType}
                onClick={handleConfirm}
              >
                {t('service.helperModal.confirmButton')}
              </KccButton>
            </div>
          </StyledHelperModal>
        </Form>
      </Modal>
    </>
  );
};
